import {
  useEffect,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import type { CarSnapshot, CarSpec, Gear, GlanceDir } from '../../sim/types';
import type { InputAction } from '../../controls/InputSource';
import type { TouchSource } from '../../controls/TouchSource';
import { useT } from '../hooks';
import type { GameControls } from './GameScreen';

function HoldButton({
  children,
  label,
  active = false,
  className = '',
  onHeld,
}: {
  children: ReactNode;
  label: string;
  active?: boolean;
  className?: string;
  onHeld: (held: boolean) => void;
}) {
  const start = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    onHeld(true);
  };
  const end = (event: ReactPointerEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onHeld(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      className={`mobile-control-button ${active ? 'is-active' : ''} ${className}`}
      onPointerDown={start}
      onPointerUp={end}
      onPointerCancel={end}
      onLostPointerCapture={() => onHeld(false)}
      onContextMenu={(event) => event.preventDefault()}
    >
      {children}
    </button>
  );
}

function ActionButton({
  children,
  label,
  active = false,
  className = '',
  onAction,
}: {
  children: ReactNode;
  label: string;
  active?: boolean;
  className?: string;
  onAction: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      className={`mobile-control-button ${active ? 'is-active' : ''} ${className}`}
      onPointerDown={(event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        event.preventDefault();
        onAction();
      }}
      onContextMenu={(event) => event.preventDefault()}
    >
      {children}
    </button>
  );
}

/** Deslizador vertical de embrague: arrastrar hacia abajo = pisar. */
function ClutchSlider({
  source,
  snap,
  spec,
  label,
}: {
  source: TouchSource;
  snap: CarSnapshot;
  spec: CarSpec;
  label: string;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const man = snap.manual;
  const m = spec.manual;
  if (!man || !m) return null;

  const valueFromEvent = (e: ReactPointerEvent<HTMLDivElement>): number => {
    const rect = railRef.current?.getBoundingClientRect();
    if (!rect || rect.height === 0) return 0;
    return Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
  };
  const start = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    source.setClutch(valueFromEvent(e));
  };
  const move = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.preventDefault();
      source.setClutch(valueFromEvent(e));
    }
  };
  const end = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    source.setClutch(null);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const pedal = man.clutchPedal;
  const biteTop = (m.clutch.bitePoint - m.clutch.biteWidth / 2) * 100;
  const biteH = m.clutch.biteWidth * 100;

  return (
    <div className="mobile-clutch" aria-label={label}>
      <div
        ref={railRef}
        className="mobile-clutch-rail"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div
          className={`mobile-clutch-bite ${man.atBite ? 'is-bite' : ''}`}
          style={{ top: `${biteTop}%`, height: `${biteH}%` }}
        />
        <div className="mobile-clutch-fill" style={{ height: `${pedal * 100}%` }} />
        <div className="mobile-clutch-knob" style={{ top: `calc(${pedal * 100}% - 13px)` }} />
      </div>
      <small>{label}</small>
    </div>
  );
}

const AUTO_GEARS: Gear[] = ['P', 'R', 'N', 'D'];
const GLANCES: Array<{ dir: Exclude<GlanceDir, 'none'>; text: string }> = [
  { dir: 'left', text: '↖' },
  { dir: 'rear', text: '↑' },
  { dir: 'right', text: '↗' },
];

export function MobileControls({
  source,
  snap,
  spec,
  controls,
}: {
  source: TouchSource;
  snap: CarSnapshot;
  spec: CarSpec;
  controls: GameControls;
}) {
  const t = useT();
  const action = (inputAction: InputAction) => source.pushAction(inputAction);
  const man = snap.manual;
  const isManual = man !== null;

  useEffect(() => {
    const release = () => source.releaseAll();
    window.addEventListener('blur', release);
    document.addEventListener('visibilitychange', release);
    return () => {
      release();
      window.removeEventListener('blur', release);
      document.removeEventListener('visibilitychange', release);
    };
  }, [source]);

  return (
    <div className="mobile-controls" aria-label={t('touch.controls')}>
      <div className="mobile-top-actions">
        <ActionButton label={t('controls.camera')} onAction={() => action({ type: 'camera' })}>
          CAM
        </ActionButton>
        <ActionButton
          label={t('controls.belt')}
          active={snap.seatbelt}
          onAction={() => action({ type: 'belt' })}
        >
          BELT
        </ActionButton>
        <ActionButton
          label={t('controls.handbrake')}
          active={snap.handbrake}
          onAction={() => action({ type: 'handbrake' })}
        >
          (P)
        </ActionButton>
        <ActionButton
          label={t('controls.lights')}
          active={snap.lights > 0}
          onAction={() => action({ type: 'lights' })}
        >
          LIGHT
        </ActionButton>
        <HoldButton
          label={t('controls.horn')}
          onHeld={(held) => action({ type: 'hornDown', down: held })}
        >
          HORN
        </HoldButton>
        <ActionButton label={t('controls.pause')} onAction={controls.togglePause}>
          Ⅱ
        </ActionButton>
      </div>

      <div className="mobile-observation-controls">
        {GLANCES.map(({ dir, text }) => (
          <HoldButton
            key={dir}
            label={t(`hud.mirror.${dir}`)}
            active={snap.glance === dir}
            onHeld={(held) => source.setGlance(dir, held)}
          >
            <span className="mobile-control-icon">{text}</span>
            <small>{t(`touch.mirror.${dir}`)}</small>
          </HoldButton>
        ))}
      </div>

      <div className="mobile-steering-controls">
        <div className="mobile-signal-row">
          <ActionButton
            label={t('touch.signal.left')}
            active={snap.signalLeft}
            onAction={() => action({ type: 'signal', side: 'left' })}
          >
            ◀
          </ActionButton>
          <ActionButton
            label={t('touch.signal.right')}
            active={snap.signalRight}
            onAction={() => action({ type: 'signal', side: 'right' })}
          >
            ▶
          </ActionButton>
        </div>
        <div className="mobile-wheel-row">
          <HoldButton label={t('touch.steer.left')} onHeld={(held) => source.setSteering('left', held)}>
            <span className="mobile-wheel-icon">↶</span>
          </HoldButton>
          <HoldButton label={t('touch.steer.right')} onHeld={(held) => source.setSteering('right', held)}>
            <span className="mobile-wheel-icon">↷</span>
          </HoldButton>
        </div>
      </div>

      {isManual ? (
        <div className="mobile-gear-controls is-manual" aria-label={t('controls.gears')}>
          <ActionButton label={t('touch.gearDown')} onAction={() => action({ type: 'gearCycle', dir: -1 })}>
            −
          </ActionButton>
          <div className="mobile-gear-display">{String(snap.gear)}</div>
          <ActionButton label={t('touch.gearUp')} onAction={() => action({ type: 'gearCycle', dir: 1 })}>
            +
          </ActionButton>
          <ActionButton
            label={`${t('touch.gear')} R`}
            active={snap.gear === 'R'}
            className="reverse-gear"
            onAction={() => action({ type: 'gear', gear: 'R' })}
          >
            R
          </ActionButton>
          <ActionButton
            label={`${t('touch.gear')} N`}
            active={snap.gear === 'N'}
            onAction={() => action({ type: 'gear', gear: 'N' })}
          >
            N
          </ActionButton>
          <HoldButton
            label={t('touch.ignition')}
            active={man !== null && !man.engineOn}
            className={man !== null && !man.engineOn ? 'ignition-alert' : ''}
            onHeld={(held) => action({ type: 'ignitionDown', down: held })}
          >
            🔑
          </HoldButton>
        </div>
      ) : (
        <div className="mobile-gear-controls" aria-label={t('controls.gears')}>
          {AUTO_GEARS.map((gear) => (
            <ActionButton
              key={String(gear)}
              label={`${t('touch.gear')} ${String(gear)}`}
              active={snap.gear === gear}
              className={gear === 'R' ? 'reverse-gear' : ''}
              onAction={() => action({ type: 'gear', gear })}
            >
              {String(gear)}
            </ActionButton>
          ))}
        </div>
      )}

      {isManual && <ClutchSlider source={source} snap={snap} spec={spec} label={t('touch.clutch')} />}

      <div className="mobile-pedal-controls">
        <HoldButton
          label={t('controls.brake')}
          active={snap.brake > 0.04}
          className="brake-pedal"
          onHeld={(held) => source.setBrake(held)}
        >
          <span>{t('touch.brake')}</span>
          <small>{Math.round(snap.brake * 100)}%</small>
        </HoldButton>
        <HoldButton
          label={t('controls.throttle')}
          active={snap.throttle > 0.04}
          className="throttle-pedal"
          onHeld={(held) => source.setThrottle(held)}
        >
          <span>{t('touch.throttle')}</span>
          <small>{Math.round(snap.throttle * 100)}%</small>
        </HoldButton>
      </div>
    </div>
  );
}
